/**
 * SEO Configuration and default values
 */

// Default SEO values
export const DEFAULT_SEO = {
  title: "FindASide - Book and Organize Sports Games",
  description:
    "FindASide helps you organize and join sports games instantly. From 5-a-side football to basketball, find your next game or fill your pitch in minutes. Automated payments, team management, and community building made easy.",
  keywords:
    "sports booking, 5-a-side football, basketball games, sports community, game organization, team management, sports payments, pickup games, recreational sports",
  ogImage: "/og-image.png", // You'll need to add this image
  siteName: "FindASide",
  siteUrl: process.env.REACT_APP_SITE_URL || "https://findaside.com",
};

// Page-specific SEO configurations
export const PAGE_SEO = {
  HOME: {
    title: "FindASide - Book Sports Games & Build Your Community",
    description:
      "Save time with FindASide. No more last-minute dropouts. Set recurring invites, automated payments, and build a reliable squad of regulars. From football to basketball - fill your games faster.",
    keywords:
      "book sports games, organize football, basketball booking, sports community, 5-a-side, team sports, pickup games, recreational sports",
  },
  ABOUT: {
    title: "About FindASide - Simplifying Sports Game Organization",
    description:
      "Learn how FindASide is revolutionizing the way people organize and join sports games. Our mission is to make sports more accessible and eliminate the hassle of game coordination.",
    keywords:
      "about findaside, sports platform, game organization, sports tech",
  },
  GAMES: {
    title: "Browse Sports Games - FindASide",
    description:
      "Discover and join sports games near you. Browse available games, check details, and book your spot instantly. Football, basketball, and more sports available.",
    keywords:
      "find sports games, join game, available games, sports near me, book game",
  },
  CREATE_GAME: {
    title: "Create a Game - FindASide",
    description:
      "Create and publish your sports game in minutes. Set up location, time, player limits, and payment options. Fill your pitch faster with automated invites.",
    keywords:
      "create game, organize sports, host game, sports event, game setup",
  },
  DASHBOARD: {
    title: "Dashboard - FindASide",
    description:
      "Manage your upcoming games, track your sports history, and connect with your sports community.",
    keywords: "dashboard, my games, sports history, game management",
  },
  PROFILE: {
    title: "My Profile - FindASide",
    description:
      "View and manage your FindASide profile, settings, and preferences.",
    keywords: "profile, user settings, account, preferences",
  },
};

// Generate dynamic SEO for game details page
export const getGameSEO = (game) => {
  if (!game) return PAGE_SEO.GAMES;

  const sportName = game.sport || "Sports";
  const location = game.location?.address || "Location";
  const date = game.game_date
    ? new Date(game.game_date).toLocaleDateString()
    : "";

  return {
    title: `${sportName} Game at ${location} - FindASide`,
    description: `Join this ${sportName} game on ${date} at ${location}. ${game.available_slots || 0} spots available. ${game.price ? `£${game.price} per player.` : ""}`,
    keywords: `${sportName.toLowerCase()}, sports game, join game, ${location}, book game`,
    ogImage: game.image_url || DEFAULT_SEO.ogImage,
  };
};

// Generate dynamic SEO for group page
export const getGroupSEO = (group) => {
  if (!group) return DEFAULT_SEO;

  return {
    title: `${group.name} - FindASide Sports Group`,
    description:
      group.description ||
      `Join ${group.name} sports group on FindASide. Connect with players and join regular games.`,
    keywords: `sports group, ${group.name}, sports community, regular games`,
  };
};

// Organization Schema.org structured data
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "FindASide",
  description: DEFAULT_SEO.description,
  url: DEFAULT_SEO.siteUrl,
  logo: `${DEFAULT_SEO.siteUrl}/logo512.png`,
  sameAs: [
    // Add your social media links here
    // "https://twitter.com/findaside",
    // "https://facebook.com/findaside",
    // "https://instagram.com/findaside",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "Customer Support",
    email: "support@findaside.com",
  },
};

// Website Schema.org structured data
export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "FindASide",
  description: DEFAULT_SEO.description,
  url: DEFAULT_SEO.siteUrl,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${DEFAULT_SEO.siteUrl}/games?search={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

// Generate Event schema for a game
export const getGameSchema = (game) => {
  if (!game) return null;

  return {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: `${game.sport} Game`,
    description:
      game.notes || `${game.sport} game at ${game.location?.address}`,
    startDate: game.datetime
      ? new Date(game.datetime).toISOString()
      : undefined,
    endDate:
      game.datetime && game.duration_minutes
        ? new Date(game.datetime + game.duration_minutes * 60000).toISOString()
        : undefined,
    location: {
      "@type": "Place",
      name: game.location?.name || game.location?.address,
      address: {
        "@type": "PostalAddress",
        streetAddress: game.location?.address,
      },
      geo: game.location?.coordinates
        ? {
            "@type": "GeoCoordinates",
            latitude: game.location.coordinates.lat,
            longitude: game.location.coordinates.lng,
          }
        : undefined,
    },
    offers: game.price
      ? {
          "@type": "Offer",
          price: game.price,
          priceCurrency: "GBP",
          availability: game.available_slots > 0 ? "InStock" : "SoldOut",
          url: `${DEFAULT_SEO.siteUrl}/game-details/${game.id}`,
        }
      : undefined,
    organizer: game.host
      ? {
          "@type": "Person",
          name: `${game.host.first_name} ${game.host.last_name}`,
        }
      : undefined,
  };
};
